import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MarketplaceLogo } from './MarketplaceLogos';
import { Store, ChevronRight, ChevronLeft } from 'lucide-react';

export default function MarketplaceCard({ mp, index = 0 }) {
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      whileHover={{ y: -10, scale: 1.03 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      <Card
        className="bg-card border-2 border-border hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500 cursor-pointer group h-full relative overflow-hidden"
        onClick={() => navigate(`/products/${mp.id}`)}
        data-testid={`marketplace-card-${mp.id}`}
      >
        {/* Hover gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-transparent to-amber-500/0 group-hover:from-primary/5 group-hover:to-amber-500/5 transition-all duration-700" />

        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-primary/0 to-transparent group-hover:via-primary transition-all duration-500" />

        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-amber-500/0 to-transparent group-hover:via-amber-500/60 transition-all duration-500" />

        <CardContent className="p-6 sm:p-8 flex flex-col h-full relative z-[1]">
          {/* Big centered logo */}
          <div className="flex flex-col items-center text-center mb-5">
            <motion.div
              className="mb-4"
              animate={hovered ? { scale: [1, 1.15, 1.1], rotate: [0, -3, 3, 0] } : { scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <div className="group-hover:drop-shadow-[0_0_15px_rgba(45,212,191,0.3)] transition-all duration-500">
                <MarketplaceLogo marketplaceId={mp.id} size={90} />
              </div>
            </motion.div>

            <motion.h3
              className="font-heading font-bold text-2xl mb-1.5 group-hover:text-primary transition-colors duration-300"
              animate={hovered ? { scale: 1.05 } : { scale: 1 }}
            >
              {isRTL ? mp.name_ar : mp.name}
            </motion.h3>
            <p className="text-sm text-muted-foreground line-clamp-2 max-w-[220px]">
              {isRTL ? mp.description_ar : mp.description}
            </p>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-5 mt-auto justify-center">
            <Badge variant="secondary" className="text-xs gap-1">
              <Store className="w-3 h-3" />
              {mp.tasks_available} {isRTL ? 'منتج' : 'products'}
            </Badge>
            <Badge variant="outline" className="text-xs text-primary border-primary/30">
              {mp.avg_commission}% {isRTL ? 'عمولة' : 'commission'}
            </Badge>
          </div>

          {/* Button */}
          <Button
            variant="secondary"
            className="w-full gap-2 h-12 rounded-xl text-sm font-semibold group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-emerald-500 group-hover:text-white group-hover:shadow-lg group-hover:shadow-primary/25 transition-all duration-500"
          >
            {isRTL ? 'تصفح المنتجات' : 'Browse Products'}
            <Chevron className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
